/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-24
 * @description Manage the lead details.
 */
import { Box, Button, Skeleton, Stack } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";

import { HeaderSection } from "./HeaderSection";
import { useFetchData } from "../../../components/hooks/useFetchData";
import { DetailsPanel } from "./DetailsPanel";
import { FormSection } from "./FormSection";
import { LoadingBackdrop } from "../../../components/common/LoadingBackdrop";

const ManageLead = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lead = location.state?.lead;
  const [minLoading, setMinLoading] = useState(true);
  const [countryList, setCountryList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [courseCountry, setCourseCountry] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState();

  const today = dayjs();

  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      candidateuuid: lead?.candidateuuid || "",
      name: lead?.name || "",
      email: lead?.email || "",
      personalphone: lead?.personalphone || "",
      enquirydate: lead?.enquirydate ? dayjs(lead.enquirydate) : null,
      ownerid: lead?.ownerid || "",
      agentid: lead?.agentid || "",
      isactive: lead?.isactive || "",
      languages: lead?.languages || "",
    },
  });

  React.useEffect(() => {
    if (lead) {
      setCourseCountry(lead?.coursecountry || []);
      setLanguages(lead?.languages || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data) => {
    const body = {
      ...data,
      enquirydate: data.enquirydate.format("YYYY-MM-DD"),
      coursecountry: courseCountry,
      languages: languages,
    };
    const result = await fetchData("managecandidate", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      navigate(-1);
    } else {
      enqueueSnackbar(result?.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  const getAllCountries = async () => {
    const result = await fetchData("getcountries?showActive=1");
    if (result?.code === 200) {
      setCountryList(result.data);
    }
  };

  // function to get languages
  const getAllLanguages = async () => {
    const result = await fetchData("getlanguages?showActive=1");
    if (result?.code === 200) {
      setLanguageList(result.data);
    }
  };

  const getCoursesByCountry = async () => {
    if (!selectedCountry) return;
    const body = { countryid: selectedCountry?.id };
    const result = await fetchData("getcoursesbycountry", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      setCourseList(result.data);
    }
  };

  const getAllUsers = async () => {
    const result = await fetchData("getusers");
    if (result?.code === 200) {
      setUserList(result.data);
    }
  };

  const getAgents = async () => {
    const result = await fetchData("getagents");
    if (result?.code === 200) {
      // setCountryList(result.data);
      setAgentList(result.data);
    }
  };

  React.useEffect(() => {
    getAllUsers();
    getAllCountries();
    getAllLanguages();
    getCoursesByCountry();
    getAgents();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      await getCoursesByCountry();
    };

    fetchDataAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const isLoading =
    minLoading || loading["getcountries?showActive=1"] || loading["getusers"];

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{ xs: "auto", md: "hidden" }}
      >
        <HeaderSection id={lead?.candidateuuid || ""} isLoading={isLoading} />
        <Stack
          pt={3}
          flex={1}
          overflow={{ xs: "unset", md: "hidden" }}
          direction={{ xs: "column", sm: "row" }}
          columnGap={1}
          rowGap={2}
        >
          <FormSection
            isLoading={isLoading}
            register={register}
            errors={errors}
            control={control}
            watch={watch}
            setValue={setValue}
            userList={userList || []}
            agentList={agentList || []}
            today={today}
          />
          <DetailsPanel
            isLoading={isLoading}
            countryList={countryList}
            courseList={courseList}
            languageList={languageList}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            languages={languages}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            courseCountry={courseCountry}
            setCourseCountry={setCourseCountry}
            setLanguages={setLanguages}
          />
        </Stack>
        <Stack
          width="100%"
          mt={2}
          direction="row"
          alignItems="center"
          justifyContent="end"
          spacing={2}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              variant="outlined"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
              onClick={() => {
                reset();
                setSelectedCountry();
                setCourseCountry(lead?.coursecountry);
              }}
            >
              Reset
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
      <LoadingBackdrop isLoading={loading["managecandidate"]} />
    </>
  );
};

export default ManageLead;
